import { create } from 'zustand';
import { LAB_STEPS, LESSON_BY_ID, LESSONS } from '../content/lessons';
import { cloneConfig, getPreset } from '../content/presets';
import type { HighlightKey } from '../content/types';
import { simClient, type FinishedRun } from '../simulation/client';
import { validateConfig } from '../simulation/simulation';
import type { ExperimentConfig } from '../simulation/types';
import {
  boundRuns,
  clearStorage,
  loadFromStorage,
  parseEnvelope,
  saveToStorage,
  serialize,
  STORAGE_KEY,
} from './persistence';
import {
  assignEvidence,
  evidenceRuns,
  expectedConfig,
  submitGate as gradeGate,
  type Answer,
  type Grade,
} from './progress';
import { compactTrace, diffPaths, expandTrace, labelPath, type RunRecord } from './runRecord';
import { emptyData, type PersistedData, type Prefs } from './types';

export type StorageStatus = 'ok' | 'unavailable' | 'failed' | 'trimmed';

export interface Notice {
  id: number;
  tone: 'info' | 'success' | 'warn' | 'error';
  text: string;
}

interface RuntimeState {
  hydrated: boolean;
  storageStatus: StorageStatus;
  storageMessage: string | null;
  notices: Notice[];
  highlight: HighlightKey | null;
  /** When true, highlight survives leaving the simulator (button-driven). */
  highlightSticky: boolean;
  cursorT: number | null;
  grades: Record<string, Record<string, Grade>>;
  gateMessages: Record<string, string | null>;
}

interface Actions {
  init(): void;
  setCurrentLesson(id: string): void;
  loadLabStep(stepId: string): void;
  loadPreset(presetId: string): void;
  updateConfig(mutate: (c: ExperimentConfig) => void): void;
  setManual(value: number): void;
  resetRun(): void;
  replay(): void;
  restoreRun(runId: string): void;
  /** Load a saved run’s settings and plots for inspection (does not re-record). */
  loadSavedRun(runId: string): void;
  recordRun(run: FinishedRun): void;
  submitGate(lessonId: string, answers: Record<string, Answer>): boolean;
  setPrediction(id: string, choice: string): void;
  setReflection(id: string, text: string): void;
  toggleCompare(runId: string): void;
  deleteRun(runId: string): void;
  setPrefs(patch: Partial<Prefs>): void;
  setHighlight(h: HighlightKey | null, sticky?: boolean): void;
  setCursor(t: number | null): void;
  notify(tone: Notice['tone'], text: string): void;
  dismiss(id: number): void;
  exportData(): string;
  importData(text: string, mode: 'replace' | 'merge'): { ok: boolean; message: string };
  resetCourse(): void;
}

export type AppState = PersistedData & RuntimeState & Actions;

let noticeId = 0;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

const persistedKeys: (keyof PersistedData)[] = [
  'currentLessonId',
  'runCounter',
  'runs',
  'progress',
  'predictions',
  'reflections',
  'comparison',
  'experiment',
  'prefs',
];

function pickPersisted(s: AppState): PersistedData {
  return Object.fromEntries(persistedKeys.map((k) => [k, s[k]])) as unknown as PersistedData;
}

/** Runs that must never be dropped: current evidence and evidence of passed gates. */
export function protectedRunIds(s: Pick<PersistedData, 'progress'>): Set<string> {
  const ids = new Set(Object.values(s.progress.evidence));
  for (const g of Object.values(s.progress.gates)) for (const id of Object.values(g.evidence ?? {})) ids.add(id);
  return ids;
}

export const useApp = create<AppState>()((set, get) => {
  const persistNow = () => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    const s = get();
    if (!s.hydrated) return;
    const data = pickPersisted(s);
    const res = saveToStorage(data, protectedRunIds(data));
    if (res.status === 'trimmed') {
      set({ runs: res.data.runs, comparison: res.data.comparison, storageStatus: 'trimmed', storageMessage: 'Storage was full, so the oldest saved runs (not needed for a lab step) were removed.' });
    } else if (res.status === 'failed') {
      set({ storageStatus: 'failed', storageMessage: 'Progress is not being saved: browser storage is full.' });
    } else if (res.status === 'unavailable') {
      set({ storageStatus: 'unavailable', storageMessage: 'Progress is not being saved: browser storage is unavailable (private window or blocked site data).' });
    } else if (s.storageStatus !== 'ok' && s.storageStatus !== 'trimmed') {
      set({ storageStatus: 'ok', storageMessage: null });
    }
  };
  const persistSoon = () => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(persistNow, 400);
  };

  const loadExperiment = (presetId: string, labStepId: string | null, config: ExperimentConfig) => {
    set({ experiment: { presetId, labStepId, config }, cursorT: null });
    simClient.load(config);
    persistSoon();
  };

  return {
    ...emptyData(),
    hydrated: false,
    storageStatus: 'ok',
    storageMessage: null,
    notices: [],
    highlight: null,
    highlightSticky: false,
    cursorT: null,
    grades: {},
    gateMessages: {},

    init() {
      if (get().hydrated) return;
      const res = loadFromStorage();
      let data = emptyData();
      if (res.status === 'ok' && res.data) data = res.data;
      const patch: Partial<AppState> = { ...data, hydrated: true };
      if (res.status === 'unavailable') {
        patch.storageStatus = 'unavailable';
        patch.storageMessage = 'Progress is not being saved: browser storage is unavailable (private window or blocked site data).';
      } else if (res.status === 'corrupt' || res.status === 'obsolete') {
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) localStorage.setItem(`${STORAGE_KEY}/unreadable-backup`, raw);
        } catch {
          /* ignore */
        }
        patch.notices = [
          {
            id: ++noticeId,
            tone: 'warn',
            text: `${res.message} A backup copy was kept in this browser, and the course was started fresh. If you exported progress earlier, you can import it from the menu.`,
          },
        ];
      }
      if (!LESSON_BY_ID[data.currentLessonId]) patch.currentLessonId = 'm0';
      set(patch);
      simClient.onFinished((run) => get().recordRun(run));
      const exp = get().experiment;
      if (exp && validateConfig(exp.config).length === 0) {
        simClient.load(exp.config); // restored paused at initial conditions
      } else {
        const first = LESSON_BY_ID[get().currentLessonId].lab.steps[0];
        get().loadLabStep(first.id);
      }
      persistNow();
    },

    setCurrentLesson(id) {
      const lesson = LESSON_BY_ID[id];
      if (!lesson) return;
      const s = get();
      if (s.currentLessonId === id) return;
      set({ currentLessonId: id });
      persistSoon();
      // Moving to another module loads its lab (the first step still missing evidence), unless already loaded.
      const activeStep = s.experiment?.labStepId ? LAB_STEPS[s.experiment.labStepId] : null;
      if (activeStep?.lesson.id !== id) {
        const step = lesson.lab.steps.find((st) => !s.progress.evidence[st.id]) ?? lesson.lab.steps[0];
        get().loadLabStep(step.id);
      }
    },

    loadLabStep(stepId) {
      const entry = LAB_STEPS[stepId];
      if (!entry) return;
      const s = get();
      const config = expectedConfig(entry.step, evidenceRuns(s.progress, s.runs));
      loadExperiment(entry.step.presetId, stepId, config);
    },

    loadPreset(presetId) {
      loadExperiment(presetId, null, cloneConfig(getPreset(presetId).config));
    },

    updateConfig(mutate) {
      const s = get();
      const exp = s.experiment;
      if (!exp) return;
      const next = cloneConfig(exp.config);
      mutate(next);
      const issues = validateConfig(next);
      if (issues.length) {
        get().notify('error', issues.map((i) => i.message).join(' '));
        return;
      }
      if (diffPaths(exp.config, next).length === 0) return;
      const snap = simClient.getSnapshot();
      const started = snap.t > 0 && snap.status === 'running';
      let labStepId = exp.labStepId;
      if (labStepId) {
        const { step } = LAB_STEPS[labStepId];
        const expected = expectedConfig(step, evidenceRuns(s.progress, s.runs));
        const bad = diffPaths(expected, next).filter((p) => !step.permitted.some((q) => p === q || p.startsWith(q + '.')));
        if (bad.length) {
          get().notify('warn', `You changed ${bad.map(labelPath).join(', ')}, which lab step "${step.title}" keeps fixed. Runs now count as free exploration, not for this step. Press Load on the step to return to the lab.`);
          labStepId = null;
        }
      }
      if (started) get().notify('info', `Settings changed at t = ${snap.t.toFixed(2)} s, so a new run started from the initial conditions. The unfinished run was discarded.`);
      loadExperiment(exp.presetId, labStepId, next);
    },

    setManual(value) {
      simClient.setManual(value);
    },

    resetRun() {
      const exp = get().experiment;
      if (exp) simClient.load(exp.config);
      set({ cursorT: null });
    },

    replay() {
      const exp = get().experiment;
      if (!exp) return;
      const events = simClient.getSnapshot().manualEvents;
      simClient.load(exp.config, events);
      set({ cursorT: null });
      simClient.play();
    },

    restoreRun(runId) {
      const r = get().runs.find((x) => x.id === runId);
      if (!r) return;
      const stepId = r.labStepId && LAB_STEPS[r.labStepId] ? r.labStepId : null;
      loadExperiment(r.presetId, stepId, cloneConfig(r.config));
      get().notify('info', `Loaded the settings of Run ${r.number}, paused at the start. Manual inputs are not included; use Replay right after a run for that.`);
    },

    loadSavedRun(runId) {
      const r = get().runs.find((x) => x.id === runId);
      if (!r) return;
      const stepId = r.labStepId && LAB_STEPS[r.labStepId] ? r.labStepId : null;
      const config = cloneConfig(r.config);
      set({ experiment: { presetId: r.presetId, labStepId: stepId, config }, cursorT: null });
      simClient.viewFinishedRun({
        config,
        samples: expandTrace(r.trace),
        status: r.status,
        stopReason: r.stopReason,
        stats: { ...r.stats },
        manualEvents: r.manualEvents,
        engine: r.engine,
      });
      get().notify(
        'info',
        `Loaded Run ${r.number} for inspection. Turn on plot options as needed, or press Reset to start a new run from these settings.`,
      );
      persistSoon();
    },

    recordRun(finished) {
      const s = get();
      const exp = s.experiment;
      if (!exp) return;
      const number = s.runCounter + 1;
      const rec: RunRecord = {
        id: `run-${Date.now().toString(36)}-${number}`,
        number,
        createdAt: Date.now(),
        presetId: exp.presetId,
        presetVersion: getPreset(exp.presetId).version,
        labStepId: exp.labStepId,
        config: cloneConfig(finished.config),
        manualEvents: finished.manualEvents.slice(),
        status: finished.status,
        stopReason: finished.stopReason,
        stats: { ...finished.stats },
        trace: compactTrace(finished.trace),
        engine: finished.engine,
      };
      const allRuns = [...s.runs, rec];
      const { stepId, reasons, frozen } = assignEvidence(rec, exp.labStepId, s.progress, allRuns);
      const progress = stepId ? { ...s.progress, evidence: { ...s.progress.evidence, [stepId]: rec.id } } : s.progress;
      const nextData = { ...pickPersisted(s), progress };
      const runs = boundRuns(allRuns, protectedRunIds(nextData));
      set({ runs, runCounter: number, progress, comparison: s.comparison.filter((id) => runs.some((r) => r.id === id)) });
      if (stepId) {
        const title = LAB_STEPS[stepId].step.title;
        get().notify('success', `Run ${number} saved and recorded for "${title}".`);
      } else if (frozen) {
        get().notify('info', `Run ${number} saved. This lab is already complete, so the recorded run stays unchanged.`);
      } else if (reasons.length && exp.labStepId) {
        get().notify('warn', `Run ${number} saved, but it does not count for "${LAB_STEPS[exp.labStepId].step.title}": ${reasons.join(' ')}`);
      } else {
        get().notify('info', `Run ${number} saved.`);
      }
      persistNow();
    },

    submitGate(lessonId, answers) {
      const lesson = LESSON_BY_ID[lessonId];
      const s = get();
      const res = gradeGate(lesson, answers, s.progress, s.runs, Date.now());
      set({
        progress: { ...s.progress, gates: { ...s.progress.gates, [lesson.gate.id]: res.record } },
        grades: { ...s.grades, [lesson.gate.id]: res.grades },
        gateMessages: { ...s.gateMessages, [lesson.gate.id]: res.blocked },
      });
      persistNow();
      return res.passed;
    },

    setPrediction(id, choice) {
      if (get().predictions[id]) return; // predictions are committed once
      set({ predictions: { ...get().predictions, [id]: choice } });
      persistSoon();
    },

    setReflection(id, text) {
      set({ reflections: { ...get().reflections, [id]: text.slice(0, 4000) } });
      persistSoon();
    },

    toggleCompare(runId) {
      const c = get().comparison;
      if (c.includes(runId)) set({ comparison: c.filter((x) => x !== runId) });
      else if (c.length >= 3) get().notify('warn', 'You can compare up to three runs. Remove one first.');
      else set({ comparison: [...c, runId] });
      persistSoon();
    },

    deleteRun(runId) {
      const s = get();
      if (protectedRunIds(pickPersisted(s)).has(runId)) {
        get().notify('warn', 'This run is recorded for a lab step, so it cannot be deleted.');
        return;
      }
      set({ runs: s.runs.filter((r) => r.id !== runId), comparison: s.comparison.filter((x) => x !== runId) });
      persistNow();
    },

    setPrefs(patch) {
      set({ prefs: { ...get().prefs, ...patch } });
      if (patch.speed !== undefined) simClient.speed = patch.speed;
      persistSoon();
    },

    setHighlight(h, sticky = false) {
      if (h === null) set({ highlight: null, highlightSticky: false });
      else set({ highlight: h, highlightSticky: sticky });
    },

    setCursor(t) {
      set({ cursorT: t });
    },

    notify(tone, text) {
      const n = { id: ++noticeId, tone, text };
      set({ notices: [...get().notices.slice(-2), n] });
      // Informational messages fade after a while; warnings and errors stay until dismissed.
      if (tone === 'info' || tone === 'success') setTimeout(() => get().dismiss(n.id), 9000);
    },

    dismiss(id) {
      set({ notices: get().notices.filter((n) => n.id !== id) });
    },

    exportData() {
      return serialize(pickPersisted(get()));
    },

    importData(text, mode) {
      const res = parseEnvelope(text);
      if (res.status !== 'ok' || !res.data) return { ok: false, message: res.message ?? 'The file could not be imported.' };
      const incoming = res.data;
      let data: PersistedData;
      if (mode === 'replace') {
        data = incoming;
      } else {
        const cur = pickPersisted(get());
        const runIds = new Set(cur.runs.map((r) => r.id));
        const runs = [...cur.runs, ...incoming.runs.filter((r) => !runIds.has(r.id))].sort((a, b) => a.createdAt - b.createdAt);
        const gates = { ...cur.progress.gates };
        for (const [id, g] of Object.entries(incoming.progress.gates)) {
          if (!gates[id] || (!gates[id].passed && g.passed)) gates[id] = g;
        }
        const evidence = { ...incoming.progress.evidence, ...cur.progress.evidence };
        // A gate taken from the import needs its own evidence.
        for (const [id, g] of Object.entries(incoming.progress.gates)) {
          if (gates[id] === g && g.evidence) Object.assign(evidence, g.evidence);
        }
        data = {
          ...cur,
          runs,
          runCounter: Math.max(cur.runCounter, incoming.runCounter),
          progress: { evidence, gates },
          predictions: { ...incoming.predictions, ...cur.predictions },
          reflections: { ...incoming.reflections, ...cur.reflections },
        };
      }
      data.runs = boundRuns(data.runs, protectedRunIds(data));
      set({ ...data, grades: {}, gateMessages: {} });
      if (data.experiment) simClient.load(data.experiment.config);
      persistNow();
      return { ok: true, message: mode === 'replace' ? 'Progress replaced from the file.' : 'Progress merged from the file.' };
    },

    resetCourse() {
      clearStorage();
      set({ ...emptyData(), grades: {}, gateMessages: {}, notices: [], cursorT: null });
      get().loadLabStep(LESSONS[0].lab.steps[0].id);
      persistNow();
      get().notify('info', 'The course was reset. All progress and saved runs were cleared.');
    },
  };
});

export const selectPersisted = pickPersisted;
